/**
 * API Service Layer for Frontend
 */
import type { DashboardStats, LeagueUpdate, OwnerLeague, PublicLeague, Organizer, OrganizerCredentials, AuditEntry, OrganizerLead, LeadCredentials } from "../types/index";
import type { PlatformAnalytics, OrganizerAnalytics, LeagueAnalytics } from "shared";

// Forzamos que siempre termine en /api para que coincida con tu backend.
// Exported so non-authenticated callers (e.g. the public landing page) can hit
// the same absolute API origin instead of relative /api paths, which would
// otherwise resolve against the web origin and return the SPA's index.html.
const rawBase = import.meta.env.VITE_API_URL || "http://localhost:3000";
export const API_BASE = rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;

// Dev-only logger. Vite statically replaces `import.meta.env.DEV` with a
// literal at build time, so these calls are tree-shaken out of production
// bundles entirely — no verbose API chatter (or payload/header leakage)
// reaches the browser console for end users.
const debugLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

const getAuthToken = () => localStorage.getItem("authToken");

const fetchAPI = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Log only the method + endpoint, never `options` — it can carry headers,
  // auth tokens, or raw request bodies we don't want surfaced.
  debugLog(`[API] Fetching ${options.method || "GET"} ${endpoint}`);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    debugLog("[API] Attaching auth token");
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });
  debugLog(`[API] Response from ${endpoint}:`, response.status, response.statusText);

  // Auto-redirect on unauthorized
  if (response.status === 401) {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("mustChangePassword");
    window.location.hash = "#login";
    throw new Error("Unauthorized");
  }

  return response;
};

export const apiService = {
  // Auth endpoints
  login: async (email: string, password: string) => {
    const response = await fetchAPI("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  // Public organizer-access lead submission (no auth). Sent from the landing
  // page form. The honeypot field is included verbatim so the server can drop
  // bot submissions. Uses a bare fetch (not fetchAPI) so it never attaches an
  // auth token or triggers the 401 -> #login redirect on this public endpoint.
  submitOrganizerLead: async (data: {
    name: string;
    organizationName: string;
    email: string;
    phone: string;
    organization_fax?: string;
  }): Promise<void> => {
    const response = await fetch(`${API_BASE}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to submit your request. Please try again.");
    }
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await fetchAPI("/leagues/stats");
    return response.json();
  },

  // League endpoints
  getLeagues: async (status: "active" | "archived" | "all" = "active") => {
    const response = await fetchAPI(`/leagues?status=${status}`);
    return response.json();
  },

  getLeagueById: async (id: number) => {
    const response = await fetchAPI(`/leagues/${id}`);
    return response.json();
  },

  // Public, no-login league snapshot reached via the sharing token. Names are
  // already obfuscated by the server. Throws on 404 so the view can show a
  // "not found" state.
  getPublicLeague: async (token: string): Promise<PublicLeague> => {
    const response = await fetchAPI(`/public/leagues/${encodeURIComponent(token)}`);
    if (!response.ok) {
      throw new Error(response.status === 404 ? "League not found" : "Failed to load league");
    }
    return response.json();
  },

  createLeague: async (data: {
    name: string;
    description?: string;
    format: "swiss" | "roundrobin" | "individual_swiss" | "individual_roundrobin";
  }) => {
    const response = await fetchAPI("/leagues", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateLeague: async (id: number, data: LeagueUpdate) => {
    const response = await fetchAPI(`/leagues/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update league");
    }
    return response.json();
  },

  archiveLeague: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/archive`, {
      method: "POST",
    });
    return response.json();
  },

  unarchiveLeague: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/unarchive`, {
      method: "POST",
    });
    return response.json();
  },

  deleteLeague: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}`, {
      method: "DELETE",
    });
    return response.json();
  },

  getStandings: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/standings`);
    const data = await response.json();
    debugLog(`[API] getStandings raw response for league ${leagueId}:`, data);

    // Patch: Sanitize NaN values in standings (e.g. 0/0 games)
    const sanitize = (item: any) => {
      if (!item) return item;
      // Fix common ratio fields if they are NaN
      ['ratio', 'winRate', 'gameRate', 'setRate'].forEach(key => {
        if (typeof item[key] === 'number' && isNaN(item[key])) item[key] = 0;
      });
      return item;
    };

    if (data.success && Array.isArray(data.data)) {
      data.data = data.data.map(sanitize);
    } else if (Array.isArray(data)) {
      return data.map(sanitize);
    }
    return data;
  },

  // Player endpoints (global registry)
  getAllPlayers: async () => {
    const response = await fetchAPI("/players");
    return response.json();
  },

  getPlayerById: async (id: number) => {
    const response = await fetchAPI(`/players/${id}`);
    return response.json();
  },

  searchPlayers: async (query: string) => {
    const response = await fetchAPI(`/players/search/${query}`);
    return response.json();
  },

  createPlayer: async (data: {
    name: string;
    level?: number;
  }) => {
    const response = await fetchAPI("/players", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updatePlayer: async (
    id: number,
    data: {
      name?: string;
      level?: number;
    }
  ) => {
    const response = await fetchAPI(`/players/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deletePlayer: async (id: number) => {
    const response = await fetchAPI(`/players/${id}`, {
      method: "DELETE",
    });
    return response.json();
  },

  // League enrollment endpoints
  getLeaguePlayers: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll`);
    return response.json();
  },

  getAvailablePlayers: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/available-players`);
    return response.json();
  },

  enrollPlayer: async (leagueId: number, playerId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll`, {
      method: "POST",
      body: JSON.stringify({ playerId }),
    });
    return response.json();
  },

  enrollPair: async (leagueId: number, playerAId: number, playerBId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll/pair`, {
      method: "POST",
      body: JSON.stringify({ playerAId, playerBId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to enroll pair");
    }
    return response.json();
  },

  // Bind two existing orphan (unpaired) enrollments into a fixed-pair team.
  pairOrphans: async (leagueId: number, playerAId: number, playerBId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll/pair-orphans`, {
      method: "POST",
      body: JSON.stringify({ playerAId, playerBId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to pair orphans");
    }
    return response.json();
  },

  // Auto-pair every orphan in a fixed-pairs league into teams (one left over if odd).
  autoPairOrphans: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll/auto-pair-orphans`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to auto-pair orphans");
    }
    return response.json();
  },

  withdrawPlayer: async (leagueId: number, playerId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/enroll/${playerId}`, {
      method: "DELETE",
    });
    return response.json();
  },

  // Round endpoints
  getRound: async (leagueId: number, roundNumber: number) => {
    // Player names arrive server-resolved in each match's `teamNames`, so no
    // client-side roster backfill is needed.
    const response = await fetchAPI(
      `/leagues/${leagueId}/rounds/${roundNumber}`
    );
    return response.json();
  },

  canScheduleRound: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/can-schedule`);
    return response.json();
  },

  generateNextRound: async (leagueId: number) => {
    const response = await fetchAPI(`/leagues/${leagueId}/rounds`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to generate round");
    }
    return response.json();
  },

  // Organizer account management (owner only; the server enforces the role).
  getOrganizers: async (): Promise<Organizer[]> => {
    const response = await fetchAPI("/owner/organizers");
    if (!response.ok) throw new Error("Failed to load organizers");
    return response.json();
  },

  // Provision a new organizer. Returns a one-time readable temporary password.
  createOrganizer: async (data: { name: string; email: string }): Promise<OrganizerCredentials> => {
    const response = await fetchAPI("/owner/organizers", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to create organizer");
    }
    return response.json();
  },

  // Toggle an organizer's active status. Deactivation revokes live sessions.
  setOrganizerStatus: async (id: number, active: boolean): Promise<{ id: number; active: boolean }> => {
    const response = await fetchAPI(`/owner/organizers/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to update status");
    }
    return response.json();
  },

  // Recent owner administrative actions for the "Recent activity" card.
  getAuditLog: async (): Promise<AuditEntry[]> => {
    const response = await fetchAPI("/owner/audit-log");
    if (!response.ok) throw new Error("Failed to load activity");
    return response.json();
  },

  // Issue a fresh one-time temporary password and force a reset on next login.
  resetOrganizerPassword: async (id: number): Promise<OrganizerCredentials> => {
    const response = await fetchAPI(`/owner/organizers/${id}/reset-password`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to reset password");
    }
    return response.json();
  },

  // Prospective-organizer leads awaiting owner triage (owner only; the server
  // enforces the role). Only pending + rejected leads are returned.
  getLeads: async (): Promise<OrganizerLead[]> => {
    const response = await fetchAPI("/owner/leads");
    if (!response.ok) throw new Error("Failed to load leads");
    return response.json();
  },

  // Approve a lead: provisions a real organizer account and returns one-time
  // credentials (name + email + readable temporary password).
  approveLead: async (id: number): Promise<LeadCredentials> => {
    const response = await fetchAPI(`/owner/leads/${id}/approve`, { method: "POST" });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to approve lead");
    }
    return response.json();
  },

  // Reject a lead (keeps it in the intake table, marked rejected).
  rejectLead: async (id: number): Promise<{ id: number; status: string }> => {
    const response = await fetchAPI(`/owner/leads/${id}/reject`, { method: "PATCH" });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to reject lead");
    }
    return response.json();
  },

  // Account self-service (owner or organizer): profile for the Account view.
  getAccount: async (): Promise<{ id: number; name: string; email: string; role: string; tenantId: string | null }> => {
    const response = await fetchAPI("/account/me");
    if (!response.ok) throw new Error("Failed to load account");
    return response.json();
  },

  // Voluntary password change from the Account view. Returns a fresh token.
  changePassword: async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    const response = await fetchAPI("/account/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to change password");
    }
    return response.json();
  },

  // Organizer forced first-login password change. Returns a fresh token.
  updateInitialPassword: async (newPassword: string, confirmPassword: string) => {
    const response = await fetchAPI("/organizer/update-initial-password", {
      method: "POST",
      body: JSON.stringify({ newPassword, confirmPassword }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to update password");
    }
    return response.json();
  },

  // Platform owner endpoints (cross-tenant; require owner role server-side)
  getOwnerLeagues: async (): Promise<OwnerLeague[]> => {
    const response = await fetchAPI("/owner/leagues");
    if (!response.ok) throw new Error("Failed to load leagues");
    return response.json();
  },

  featureLeague: async (leagueId: number) => {
    const response = await fetchAPI(`/owner/leagues/${leagueId}/feature`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to feature league");
    }
    return response.json();
  },

  unfeatureLeague: async (leagueId: number) => {
    const response = await fetchAPI(`/owner/leagues/${leagueId}/unfeature`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to unfeature league");
    }
    return response.json();
  },

  // Insights / analytics endpoints
  // Platform-wide metrics (owner only; the server enforces the role).
  getPlatformAnalytics: async (): Promise<PlatformAnalytics> => {
    const response = await fetchAPI("/admin/analytics");
    if (!response.ok) throw new Error("Failed to load platform analytics");
    return response.json();
  },

  // Metrics scoped to the signed-in organizer's own tenant.
  getOrganizerAnalytics: async (): Promise<OrganizerAnalytics> => {
    const response = await fetchAPI("/organizer/analytics");
    if (!response.ok) throw new Error("Failed to load insights");
    return response.json();
  },

  // Granular operational insights for ONE league the organizer owns.
  getLeagueAnalytics: async (leagueId: number): Promise<LeagueAnalytics> => {
    const response = await fetchAPI(`/organizer/leagues/${leagueId}/analytics`);
    if (!response.ok) throw new Error("Failed to load league insights");
    return response.json();
  },

  // Match endpoints
  updateMatch: async (
    matchId: number,
    data: any
  ) => {
    const response = await fetchAPI(`/matches/${matchId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update match");
    }
    
    return response.json();
  },
};
